import { WarningFilled, type CarbonIconType } from '@carbon/icons-react';
import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cx } from '@/shared/lib/cx';
import { useFormField } from './FormField';
import s from './Input.module.css';

export type ControlSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: ControlSize;
  invalid?: boolean;
  /** Иконка слева (поиск и т. п.). */
  leadingIcon?: CarbonIconType;
  /** Текст справа внутри поля (единицы: «₸», «мин»). */
  suffix?: ReactNode;
  /** Кнопка справа внутри поля (очистка, календарь). */
  trailing?: ReactNode;
  containerClassName?: string;
}

/** Атрибуты доступности из FormField, объединённые с явно переданными. */
export function useFieldAttributes(props: {
  id?: string | undefined;
  invalid?: boolean | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  'aria-describedby'?: string | undefined;
}) {
  const field = useFormField();
  const invalid = props.invalid ?? field?.invalid ?? false;
  const describedBy = [field?.describedBy, props['aria-describedby']].filter(Boolean).join(' ') || undefined;
  return {
    field,
    id: props.id ?? field?.id,
    invalid,
    required: props.required ?? field?.required ?? false,
    disabled: props.disabled ?? field?.disabled ?? false,
    describedBy,
  };
}

/** Однострочное поле ввода. Размер текста 16px — iOS не увеличивает страницу при фокусе. */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', invalid: invalidProp, leadingIcon: LeadingIcon, suffix, trailing, containerClassName, className, id: idProp, required: requiredProp, disabled: disabledProp, readOnly, type, ...rest },
  ref,
) {
  const { id, invalid, required, disabled, describedBy } = useFieldAttributes({
    id: idProp,
    invalid: invalidProp,
    required: requiredProp,
    disabled: disabledProp,
    'aria-describedby': rest['aria-describedby'],
  });
  return (
    <div
      className={cx(s.control, size !== 'md' && s[size], containerClassName)}
      data-invalid={invalid ? 'true' : undefined}
      data-disabled={disabled ? 'true' : undefined}
      data-readonly={readOnly ? 'true' : undefined}
    >
      {LeadingIcon ? <LeadingIcon size={16} className={s.icon} aria-hidden="true" /> : null}
      <input
        ref={ref}
        id={id}
        type={type ?? 'text'}
        className={cx(s.input, className)}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        {...rest}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
      />
      {invalid ? <WarningFilled size={16} className={s.invalidIcon} aria-hidden="true" /> : null}
      {suffix ? (
        <span className={s.suffix} aria-hidden="true">
          {suffix}
        </span>
      ) : null}
      {trailing ? <div className={s.trailing}>{trailing}</div> : null}
    </div>
  );
});
